import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Globe } from 'lucide-react';
import api from '../services/api';
import { useToast } from '../hooks/use-toast';

const SkillsPage = () => {
  const [portfolioData, setPortfolioData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        setLoading(true);
        const data = await api.portfolio.getAll();
        setPortfolioData(data);
      } catch (error) {
        console.error('Error fetching portfolio:', error);
        toast({
          title: 'Error',
          description: 'Failed to load portfolio data.',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolio();
  }, [toast]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!portfolioData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-gray-600">Failed to load data.</p>
      </div>
    );
  }

  return (
    <section className="py-20 bg-transparent">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Skills & Expertise</h1>
          <div className="w-20 h-1 bg-blue-600 mx-auto"></div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {Object.entries(portfolioData.skills).filter(([category]) => !['AI Tools', 'Adaptation Strategies', 'Additional Subjects'].includes(category)).map(([category, skills], index) => (
            <Card 
              key={index} 
              className={`border-blue-100 hover:shadow-lg transition-shadow duration-300 ${
                category === 'Science Labs' ? 'md:col-span-2 lg:col-span-4' : ''
              }`}
            >
              <CardHeader>
                <CardTitle className="text-lg text-blue-900">{category}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {skills.map((skill, sIndex) => {
                    let badgeClass = "bg-blue-100 text-blue-700 hover:bg-blue-200";
                    if (category === 'Science Labs') {
                      if (skill.startsWith('Chemistry:')) {
                        badgeClass = "bg-pink-100 text-pink-700 hover:bg-pink-200";
                      } else if (skill.startsWith('Physics:')) {
                        badgeClass = "bg-gray-100 text-gray-700 hover:bg-gray-200";
                      } else if (skill.startsWith('Biology:')) {
                        badgeClass = "bg-green-100 text-green-700 hover:bg-green-200";
                      } else {
                        badgeClass = "bg-blue-100 text-blue-700 hover:bg-blue-200";
                      }
                    } else if (category === 'Additional Subjects') {
                      badgeClass = "bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-300";
                    }
                    
                    return (
                      <Badge key={sIndex} variant="secondary" className={`${badgeClass} transition-colors`}>
                        {skill}
                      </Badge>
                    );
                  })}
                </div>
                
                {/* Additional Subjects sub-section within Subject Areas */}
                {category === 'Subject Areas' && portfolioData.skills['Additional Subjects'] && (
                  <div className="mt-4 pt-4 border-t border-blue-200">
                    <div className="text-sm font-semibold text-gray-600 mb-2">Additional Teaching Experience</div>
                    <div className="flex flex-wrap gap-2">
                      {portfolioData.skills['Additional Subjects'].map((subject, subIndex) => (
                        <Badge key={subIndex} variant="secondary" className="bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-300 transition-colors">
                          {subject}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {category === 'Pedagogical Approaches' && portfolioData.skills['Adaptation Strategies'] && (
                  <div className="mt-4 pt-4 border-t border-blue-200">
                    <div className="text-sm font-semibold text-blue-900 mb-2">Adaptation Strategies</div>
                    <div className="flex flex-wrap gap-2">
                      {portfolioData.skills['Adaptation Strategies'].map((strategy, stratIndex) => (
                        <Badge key={stratIndex} variant="secondary" className="bg-green-600 text-white hover:bg-green-700 transition-colors">
                          {strategy}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {category === 'Technology & Tools' && portfolioData.skills['AI Tools'] && (
                  <div className="mt-4 pt-4 border-t border-blue-200">
                    <div className="text-sm font-semibold text-blue-900 mb-2">AI Tools</div>
                    <div className="flex flex-wrap gap-2">
                      {portfolioData.skills['AI Tools'].map((aiTool, aiIndex) => (
                        <Badge key={aiIndex} variant="secondary" className="bg-blue-600 text-white hover:bg-blue-700 transition-colors">
                          {aiTool}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Languages */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center flex items-center justify-center gap-2">
            <Globe className="w-6 h-6 text-blue-600" />
            Languages
          </h2>
          <div className="flex flex-wrap justify-center gap-6 max-w-4xl mx-auto">
            {portfolioData.languages.map((lang, index) => (
              <Card key={index} className="border-blue-100 text-center hover:shadow-lg transition-shadow duration-300 w-56">
                <CardHeader>
                  <CardTitle className="text-lg text-blue-900">{lang.name}</CardTitle>
                  <p className="text-gray-600">{lang.level}</p>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SkillsPage;
